import { computed, set } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import type { ChapterThemeDefaults } from '@/composables/payment'
import { STORAGE_CUSTOM_THEME } from '@/constants'
import { clamp } from '@/utils'
import { brand } from '@/config/brands'
import { loadBrandConfig } from '@/utils/loadBrandConfig'
import { NOVELMELLO_READER_TYPOGRAPHY } from '@/pages/chapter/configs/novelmello'

/**
 * 主题管理 Composable
 * 管理阅读器的主题颜色和字体大小设置
 */

// 字体大小映射表 (14 个选项)
export const THEME_FONT_MAP = ['24', '26', '28', '30', '32', '34', '36', '38', '40', '42', '44', '46', '48', '50']

// 主题背景颜色 (5 种)
export const THEME_BACKGROUNDS = ['#F6F7F9', '#CEE7FF', '#FFE1DC', '#FBE6B5', '#292929']

// 皮肤主题列表（每个皮肤关联对应的背景主题色）
export const THEME_SKINS = [
  { id: 'skin-1', thumb: `/images/${brand}/chapter/skin-1-thumb.png`, bg: `/images/${brand}/chapter/skin-1.webp`, background: '#FDF2E8' },
  { id: 'skin-2', thumb: `/images/${brand}/chapter/skin-2-thumb.png`, bg: `/images/${brand}/chapter/skin-2.webp`, background: '#D5E9D4' },
]

/** 阅读排版参数(正文/标题字体族、字重、行高) */
interface ReaderTypography {
  fontFamily: string
  /** 章标题相对正文的字号增量,单位 px */
  titleSizeDelta: number
  titleFontWeight: number | string
  /** 不配置时不输出该声明,保持现状 */
  titleLineHeight?: string | number
  contentLineHeight: string | number
  /** 首次进入时的字号档位;不配置时沿用品牌 themeDefaults.size */
  defaultSize?: string
}

/** 存量品牌排版:与历史输出逐字符一致(Georgia / 标题 +2px / 500 / 正文 1.7em) */
const DEFAULT_READER_TYPOGRAPHY: ReaderTypography = {
  fontFamily: 'Georgia, "Times New Roman", Times, serif',
  titleSizeDelta: 2,
  titleFontWeight: 500,
  contentLineHeight: '1.7em',
}

const MOONPAGE_READER_TYPOGRAPHY: ReaderTypography = {
  ...DEFAULT_READER_TYPOGRAPHY,
  defaultSize: '40',
}

// 排版参数按品牌显式注册(novelmello 键固定放在末尾,避免其它品牌 pickFirstDefined 误取)
const READER_TYPOGRAPHY = loadBrandConfig<ReaderTypography>({
  rocnovel: DEFAULT_READER_TYPOGRAPHY,
  oceread: DEFAULT_READER_TYPOGRAPHY,
  talewave: DEFAULT_READER_TYPOGRAPHY,
  ficmoon: DEFAULT_READER_TYPOGRAPHY,
  novelarc: DEFAULT_READER_TYPOGRAPHY,
  storyella: DEFAULT_READER_TYPOGRAPHY,
  aurenovel: DEFAULT_READER_TYPOGRAPHY,
  florastory: DEFAULT_READER_TYPOGRAPHY,
  silktale: DEFAULT_READER_TYPOGRAPHY,
  pearla: DEFAULT_READER_TYPOGRAPHY,
  fictora: DEFAULT_READER_TYPOGRAPHY,
  marinovel: DEFAULT_READER_TYPOGRAPHY,
  flipnovel: DEFAULT_READER_TYPOGRAPHY,
  moonpage: MOONPAGE_READER_TYPOGRAPHY,
  storlyn: DEFAULT_READER_TYPOGRAPHY,
  novaris: DEFAULT_READER_TYPOGRAPHY,
  novelmello: NOVELMELLO_READER_TYPOGRAPHY,
})

export function useChapterTheme(defaults?: ChapterThemeDefaults) {
  // 从 localStorage 读取主题设置,使用品牌默认值或通用默认值
  const theme = useLocalStorage(STORAGE_CUSTOM_THEME, {
    background: defaults?.background || '#F6F7F9',
    size: READER_TYPOGRAPHY.defaultSize || defaults?.size || '38',
    skinId: '' as string, // 皮肤主题 ID: '' | 'skin-1' | 'skin-2'
  })

  // 计算字体样式
  const THEME_FONT = computed(() => {
    // 确保字体大小在有效范围内
    if (!THEME_FONT_MAP.includes(theme.value.size))
      set(theme.value, 'size', '38')

    const fontSize = Number.parseInt(theme.value.size)

    const { fontFamily, titleSizeDelta, titleFontWeight, titleLineHeight, contentLineHeight } = READER_TYPOGRAPHY
    const titleLineHeightCss = titleLineHeight ? `line-height: ${titleLineHeight};` : ''

    return {
      chapterTitle: `font-size: ${fontSize / 2 + titleSizeDelta}px;font-weight: ${titleFontWeight};${titleLineHeightCss}font-family: ${fontFamily};`,
      chapterContent: `margin-top: 1em;font-size: ${fontSize / 2}px;line-height: ${contentLineHeight};font-family: ${fontFamily};`,
      leadSentence: `font-size: ${fontSize / 2 - 1}px;line-height: 1.7em;font-family: ${fontFamily};`,
      paragraphSpacing: 'margin-top: 1em;',
    }
  })

  /**
   * 改变字体大小
   * @param zoom 缩放方向 (1: 放大, -1: 缩小)
   */
  function onChangeThemeSize(zoom: number) {
    const currentIndex = THEME_FONT_MAP.indexOf(theme.value.size)
    const newIndex = clamp(currentIndex + zoom, 0, THEME_FONT_MAP.length - 1)
    set(theme.value, 'size', THEME_FONT_MAP[newIndex])
  }

  /**
   * 设置背景颜色
   * @param color 背景颜色值
   */
  function setBackground(color: string) {
    if (THEME_BACKGROUNDS.includes(color))
      theme.value.background = color
  }

  /**
   * 设置皮肤主题，同时切换对应的背景主题色
   * @param skinId 皮肤 ID
   */
  function setSkin(skinId: string) {
    if (theme.value.skinId === skinId) {
      set(theme.value, 'skinId', '')
    }
    else {
      const skin = THEME_SKINS.find(s => s.id === skinId)
      set(theme.value, 'skinId', skinId)
      if (skin?.background)
        theme.value.background = skin.background
    }
  }

  // 当前皮肤背景图
  const currentSkinBg = computed(() => {
    const skin = THEME_SKINS.find(s => s.id === theme.value.skinId)
    return skin?.bg || ''
  })

  return {
    theme,
    THEME_FONT,
    THEME_FONT_MAP,
    THEME_BACKGROUNDS,
    THEME_SKINS,
    onChangeThemeSize,
    setBackground,
    setSkin,
    currentSkinBg,
  }
}
